import { useEffect, useMemo, useState } from "react";
import Footer from "../components/home/Footer";
import Header from "../components/home/Header";
import MiniCart from "../components/home/MiniCart";
import SEOHead from "../components/seo/SEOHead";

const INITIAL_POST_COUNT = 20;
const POSTS_PER_LOAD = 6;

function formatDate(date) {
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(new Date(date));
}

function getBlogUrl(post) {
  return `/blog/${post.slug}`;
}

function BlogCard({ post }) {
  const url = getBlogUrl(post);

  return (
    <article className="blog-list-card" itemScope itemType="https://schema.org/BlogPosting">
      <a className="blog-list-image-link" href={url} aria-label={post.title}>
        <img src={post.image} alt={post.title} itemProp="image" />
      </a>
      <div className="blog-list-card-body">
        <div className="blog-list-meta">
          <span itemProp="articleSection">{post.category}</span>
          <span>{post.readTime}</span>
        </div>
        <h2 className="blog-list-card-title" itemProp="headline">
          <a href={url}>{post.title}</a>
        </h2>
        <p itemProp="description">{post.excerpt}</p>
        <a className="blog-list-read" href={url}>Read post -></a>
      </div>
    </article>
  );
}

export default function BlogListingPage() {
  const [posts, setPosts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_POST_COUNT);
  const [scrolled, setScrolled] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let active = true;

    fetch("/json/blogs.json")
      .then((response) => response.json())
      .then((data) => {
        if (!active) return;
        setPosts([...data].sort((a, b) => new Date(b.date) - new Date(a.date)));
      })
      .catch(() => {
        if (active) setPosts([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const [latestPost, ...olderPosts] = posts;
  const visiblePosts = olderPosts.slice(0, visibleCount);
  const hasMore = visibleCount < olderPosts.length;

  const schema = useMemo(() => {
    const itemList = visiblePosts.map((post, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": `https://parasorganics.com${getBlogUrl(post)}`,
      "name": post.title
    }));

    return [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Paras Organics",
        "url": "https://parasorganics.com",
        "logo": "https://parasorganics.com/logo512.png"
      },
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Paras Organics Blog",
        "description": "Organic farming, vermicompost, Vermiwash, home gardening, and soil health guides from Paras Organics.",
        "url": "https://parasorganics.com/blog",
        "publisher": {
          "@type": "Organization",
          "name": "Paras Organics"
        },
        "blogPost": posts.slice(0, 10).map((post) => ({
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.image,
          "datePublished": post.date,
          "dateModified": post.date,
          "author": {
            "@type": "Organization",
            "name": post.author
          },
          "url": `https://parasorganics.com${getBlogUrl(post)}`
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Paras Organics Blog Articles",
        "itemListElement": itemList
      }
    ];
  }, [posts, visiblePosts]);

  return (
    <>
      <SEOHead
        title="Organic Farming & Gardening Blog"
        description="Read Paras Organics guides on vermicompost, Vermiwash, home gardening, agriculture, landscaping, and natural soil health."
        path="/blog"
        image={latestPost?.image}
        schema={schema}
      />
      <Header scrolled={scrolled} cartItems={cartItems} onCartClick={() => setCartOpen((open) => !open)} />
      {cartOpen && <MiniCart cartItems={cartItems} onClose={() => setCartOpen(false)} />}
      <main className="blog-list-page">
        <section className="blog-list-hero" aria-labelledby="blog-list-title">
          <div className="section-inner">
            <span className="section-label">Paras Organics Blog</span>
            <h1 className="section-title" id="blog-list-title">Organic Growing Guides</h1>
            <p className="section-desc">Practical advice for soil health, vermicompost, Vermiwash, home gardens, farms, nurseries, and landscaping projects.</p>
          </div>
        </section>

        {latestPost && (
          <section className="blog-list-feature-section" aria-label="Latest blog post">
            <div className="section-inner">
              <article className="blog-list-feature" itemScope itemType="https://schema.org/BlogPosting">
                <a className="blog-list-feature-image" href={getBlogUrl(latestPost)} aria-label={latestPost.title}>
                  <img src={latestPost.image} alt={latestPost.title} itemProp="image" />
                </a>
                <div className="blog-list-feature-content">
                  <div className="blog-list-meta">
                    <span itemProp="articleSection">{latestPost.category}</span>
                    <span>{latestPost.readTime}</span>
                  </div>
                  <h2 itemProp="headline">
                    <a href={getBlogUrl(latestPost)}>{latestPost.title}</a>
                  </h2>
                  <p itemProp="description">{latestPost.excerpt}</p>
                  <div className="blog-list-date">
                    Published on <time dateTime={latestPost.date} itemProp="datePublished">{formatDate(latestPost.date)}</time>
                  </div>
                  <a className="blog-list-read" href={getBlogUrl(latestPost)}>Read post -></a>
                </div>
              </article>
            </div>
          </section>
        )}

        <section className="blog-list-grid-section" aria-label="Blog articles">
          <div className="section-inner">
            <div className="blog-list-grid">
              {visiblePosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>

            {hasMore && (
              <div className="blog-list-load-wrap">
                <button
                  className="btn-outline-green"
                  type="button"
                  onClick={() => setVisibleCount((count) => count + POSTS_PER_LOAD)}
                >
                  Older posts ->
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
